/**
 * Business Hours Page
 * Shows weekly business hours schedule for a location with editing capabilities
 */

import { useState, useEffect, useMemo } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { SettingsPageHeader } from '@/components/ui';
import { useGetLocations } from '@/api/generated/locations/locations';
import { BusinessHoursCard } from './business-setup/tabs/business-hours-card';
import { BusinessHoursLocationPicker } from './business-setup/tabs/business-hours-location-picker';
import { BusinessSetupPageShell } from './business-setup/components/business-setup-page-shell';
import { getDefaultBusinessHoursLocationId } from './business-setup/utils/default-business-hours-location';
import { salonWeekStartFromDateKey, getWeekEnd, formatDateRangeHeader } from './staff/utils/date-time';
import { useSalonToday } from '@/hooks/use-salon-timezone';
import { formatDocumentTitle } from '@/lib/document-title';

export const Route = createFileRoute('/_auth/$orgId/settings/business-setup/business-hours')({
  component: BusinessHoursPage,
});

function BusinessHoursPage() {
  const { orgId } = useParams({ from: '/_auth/$orgId/settings/business-setup/business-hours' });
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  // Weeks from the salon's current week; the grid waits for the salon's today (never the viewer's).
  const salonToday = useSalonToday();
  const [weekOffset, setWeekOffset] = useState(0);
  const currentWeekStart = useMemo(
    () => (salonToday ? salonWeekStartFromDateKey(salonToday, weekOffset) : null),
    [salonToday, weekOffset]
  );

  // Fetch locations for dropdown
  const { data: locationsResponse, isLoading: isLoadingLocations } = useGetLocations();

  // Memoize locations to avoid exhaustive-deps warning
  const locations = useMemo(() => locationsResponse?.data ?? [], [locationsResponse?.data]);

  // Auto-select first location when loaded
  useEffect(() => {
    const defaultLocationId = getDefaultBusinessHoursLocationId(locations);
    if (!selectedLocationId && defaultLocationId) setSelectedLocationId(defaultLocationId);
  }, [locations, selectedLocationId]);

  // Week navigation
  const weekEnd = currentWeekStart ? getWeekEnd(currentWeekStart) : null;
  const handlePrevWeek = () => setWeekOffset((offset) => offset - 1);
  const handleNextWeek = () => setWeekOffset((offset) => offset + 1);

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Business Hours')}</title>
      </Helmet>

      <BusinessSetupPageShell orgId={orgId} activePath="business-hours">
        {/* Page Header */}
        <SettingsPageHeader
          title="Business Hours"
          description="Set the opening hours for your business location."
        />

        {/* Location Selector + Week Navigation Row */}
        <div className="mb-4 flex max-w-2xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {/* Location Selector (text-based with dropdown) */}
          <BusinessHoursLocationPicker
            locations={locations}
            selectedLocationId={selectedLocationId}
            isLoading={isLoadingLocations}
            onSelect={setSelectedLocationId}
          />

          {/* Week Navigation */}
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <span className="mr-auto text-[16px] font-medium text-clovie-muted sm:mr-2">
              {currentWeekStart && weekEnd ? formatDateRangeHeader(currentWeekStart, weekEnd) : ''}
            </span>
            <button
              type="button"
              onClick={handlePrevWeek}
              className="flex h-11 w-11 items-center justify-center rounded-[var(--r-pill)] border border-clovie-border bg-clovie-surface text-clovie-muted shadow-soft transition-colors hover:border-clovie-brand-soft hover:bg-clovie-hover hover:text-clovie-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clovie-brand focus-visible:ring-offset-2 focus-visible:ring-offset-clovie-surface"
              aria-label="Previous week"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleNextWeek}
              className="flex h-11 w-11 items-center justify-center rounded-[var(--r-pill)] border border-clovie-border bg-clovie-surface text-clovie-muted shadow-soft transition-colors hover:border-clovie-brand-soft hover:bg-clovie-hover hover:text-clovie-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clovie-brand focus-visible:ring-offset-2 focus-visible:ring-offset-clovie-surface"
              aria-label="Next week"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Business Hours Card */}
        <div className="max-w-2xl">
          {selectedLocationId && currentWeekStart ? (
            <BusinessHoursCard
              locationId={selectedLocationId}
              currentWeekStart={currentWeekStart}
            />
          ) : (
            <div className="rounded-[var(--r-2xl)] border border-clovie-border-soft bg-clovie-surface p-12 text-center text-clovie-muted shadow-soft">
              {isLoadingLocations || (selectedLocationId && !currentWeekStart)
                ? 'Loading...'
                : 'No location selected. Please select a location above.'}
            </div>
          )}
        </div>
      </BusinessSetupPageShell>
    </>
  );
}
